import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Package, Eye, ShoppingCart, Edit3, Trash2, ShieldCheck,
  Camera, ArrowLeft, Loader2, MapPin, Megaphone, LogOut, Store as StoreIcon, ImageIcon,
  ShoppingBag, TrendingUp, Users, Search, Star, Zap, BarChart3, CheckCircle2,
  Filter, Phone, Clock, RefreshCw, ChevronRight, Info,
} from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, XAxis, Tooltip } from "recharts";
import AboutArteknosModal from "@/components/AboutArteknosModal";
import useAboutOnce from "@/hooks/useAboutOnce";
import RoleSwitcher from "@/components/RoleSwitcher";
import { AccountMenu } from "@/components/AccountMenu";
import { PartnerMapPanel } from "@/components/map/PartnerMapPanel";
import { SubscriptionWalletCard } from "@/components/subscription/SubscriptionWalletCard";
import { RestaurantMenuManager } from "@/components/restaurant/RestaurantMenuManager";
import { RestaurantOrdersPanel } from "@/components/restaurant/RestaurantOrdersPanel";
import { UtensilsCrossed, ChefHat } from "lucide-react";
import { RestaurantInfoButton } from "@/components/restaurant/RestaurantInfoButton";
import { PartnerWalletPanel } from "@/components/partner/PartnerWalletPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatFCFA = (n) => Number(n || 0).toLocaleString("fr-FR");

// Calcule début/fin du mois courant en ISO pour filtrer les commandes
const monthBoundsISO = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
  return start;
};

export default function PartnerDashboard({ user, onLogout, onUserUpdate }) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // Mode global de la page
  const [mode, setMode] = useState("dashboard"); // dashboard | add-product
  const [activeTab, setActiveTab] = useState("stocks"); // stocks | ventes | promos | menu | cuisine
  const isRestaurant = user?.business_type === "restaurant";
  const aboutModal = useAboutOnce(user?.id);

  // Inventaire
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);

  // Commandes (pour CA et stats)
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({
    monthlyRevenue: 0,
    monthlyOrders: 0,
    totalViews: 0,
    pendingOrders: 0,
  });

  // Form ajout produit
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    price: "",
    stock: "",
    description: "",
    category: "boutique",
    image_url: "",
  });

  const fetchProducts = useCallback(async () => {
    setLoadingProducts(true);
    try {
      const r = await axios.get(`${API}/products?vendor_id=${user.id}`);
      setProducts(r.data || []);
    } catch (e) {
      console.warn("Failed to load products:", e);
    } finally {
      setLoadingProducts(false);
    }
  }, [user.id]);

  const fetchOrdersAndStats = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/orders?role=partner&partner_id=${user.id}&limit=200`);
      const all = r.data || [];
      setOrders(all);
      const monthStart = monthBoundsISO();
      const monthOrders = all.filter((o) => {
        const created = o.created_at || o.createdAt;
        return created && created >= monthStart;
      });
      const monthDelivered = monthOrders.filter((o) => o.status === "delivered");
      const ca = monthDelivered.reduce((sum, o) => sum + (o.items_amount || o.total_amount || 0), 0);
      setStats((prev) => ({
        ...prev,
        monthlyRevenue: ca,
        monthlyOrders: monthOrders.length,
        pendingOrders: all.filter((o) => o.status === "pending").length,
      }));
    } catch (e) {
      console.warn("Failed to load orders:", e);
    }
  }, [user.id]);

  useEffect(() => {
    fetchProducts();
    fetchOrdersAndStats();
  }, [fetchProducts, fetchOrdersAndStats]);

  // Total cumulé des vues sur tous les produits
  useEffect(() => {
    const totalViews = products.reduce((s, p) => s + (p.views_count || 0), 0);
    setStats((prev) => ({ ...prev, totalViews }));
  }, [products]);

  // ========== Image upload ==========
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image trop volumineuse (max 5 Mo)");
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast.error("Format d'image invalide");
      return;
    }
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("vendor_id", user.id);
      const r = await axios.post(`${API}/products/upload-image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (r.data?.success) {
        const fullUrl = `${BACKEND_URL}${r.data.image_url}`;
        setFormData((prev) => ({ ...prev, image_url: fullUrl }));
        toast.success("Image téléchargée");
      }
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("Erreur lors du téléchargement de l'image");
    } finally {
      setUploadingImage(false);
    }
  };

  const submitProduct = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.price || !formData.stock) {
      toast.error("Nom, prix et stock requis");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/products`, {
        vendor_id: user.id,
        name: formData.name.trim(),
        description: formData.description?.trim() || "",
        price: parseInt(formData.price, 10),
        stock: parseInt(formData.stock, 10),
        category: formData.category,
        image_url: formData.image_url || "",
      });
      toast.success("Produit mis en ligne !");
      setFormData({ name: "", price: "", stock: "", description: "", category: "boutique", image_url: "" });
      setMode("dashboard");
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setSubmitting(false);
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Supprimer ce produit ?")) return;
    try {
      await axios.delete(`${API}/products/${id}`);
      toast.success("Produit supprimé");
      fetchProducts();
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Marquer une commande comme prête (déclenche broadcast livreur en backend)
  const markOrderReady = async (orderId) => {
    try {
      const res = await axios.put(`${API}/orders/${orderId}/mark-ready`, null, {
        params: { partner_id: user.id },
      });
      const notified = res.data?.couriers_notified ?? 0;
      if (notified > 0) {
        toast.success(
          `Expédition confirmée — ${notified} livreur${notified > 1 ? "s" : ""} notifié${notified > 1 ? "s" : ""} (rayon ${res.data.broadcast_radius_km || 5} km)`
        );
      } else {
        toast.success("Commande prête — en attente d'un livreur disponible");
      }
      fetchOrdersAndStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors du marquage");
    }
  };

  // ========== UI ==========
  return (
    <div className="min-h-screen bg-[#020617] text-white font-sans pb-28" data-testid="partner-dashboard">
      <AboutArteknosModal open={aboutModal.open} onClose={aboutModal.close} />
      {/* HEADER PREMIUM */}
      <header className="p-5 md:p-6 bg-[#020617]/80 backdrop-blur-xl border-b border-white/5 flex justify-between items-center sticky top-0 z-50">
        <div className="flex items-center gap-3 md:gap-4">
          <img
            src="/woma-logo.png"
            alt="WÔMA de ARTEKNOS"
            className="w-11 h-11 md:w-12 md:h-12 rounded-full object-cover ring-2 ring-[#D4AF37]/50 shadow-[0_0_14px_rgba(212,175,55,0.35)] flex-shrink-0"
            data-testid="partner-header-logo"
          />
          <div>
            <h1
              className="text-base md:text-xl font-black italic tracking-tight uppercase text-white"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              {user?.business_name || "WÔMA Shop"}
            </h1>
            <p className="text-[9px] md:text-[10px] text-blue-400 font-bold tracking-[0.3em] uppercase">
              Partner ID · #WOMA-{(user?.id || "00000").substring(0, 5).toUpperCase()}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 md:gap-4">
          {isRestaurant && <RestaurantInfoButton />}
          <button
            onClick={aboutModal.openManually}
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-[10px] font-black uppercase tracking-widest text-slate-300 hover:text-white"
            data-testid="partner-about-btn"
            title="Qui sommes-nous ?"
          >
            <Info size={12} /> Qui sommes-nous ?
          </button>
          <button
            onClick={aboutModal.openManually}
            className="sm:hidden w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 hover:text-white flex items-center justify-center"
            data-testid="partner-about-btn-mobile"
            aria-label="Qui sommes-nous ?"
          >
            <Info size={14} />
          </button>
          <div className="hidden lg:flex items-center gap-3 bg-white/5 px-4 py-2 rounded-xl border border-white/10">
            <Search size={14} className="text-slate-400" />
            <input
              type="text"
              placeholder="Rechercher une commande…"
              className="bg-transparent border-none outline-none text-xs w-48 text-white placeholder:text-slate-500"
              data-testid="partner-search-input"
            />
          </div>
          <div className="hidden md:flex flex-col items-end px-3 py-1.5 rounded-xl border border-emerald-500/30 bg-emerald-500/5" data-testid="partner-monthly-revenue">
            <span className="text-[8px] font-black text-emerald-400 uppercase tracking-widest">CA mois</span>
            <span className="text-xs font-black text-emerald-300">{formatFCFA(stats.monthlyRevenue)} F</span>
          </div>
          <RoleSwitcher user={user} onUserUpdate={onUserUpdate} className="hidden sm:block" />
          <button
            onClick={onLogout}
            className="hidden sm:block p-2.5 rounded-xl bg-white/5 border border-white/10 text-slate-400 hover:text-red-400 hover:border-red-400/40"
            data-testid="partner-logout-btn"
          >
            <LogOut size={16} />
          </button>
          <AccountMenu user={user} onUserUpdate={onUserUpdate} onLogout={onLogout} className="sm:hidden" />
        </div>
      </header>

      <main className="p-5 md:p-8 max-w-7xl mx-auto">
        {mode === "add-product" ? (
          <AddProductView
            formData={formData}
            setFormData={setFormData}
            uploadingImage={uploadingImage}
            submitting={submitting}
            fileInputRef={fileInputRef}
            handleImageUpload={handleImageUpload}
            onSubmit={submitProduct}
            onBack={() => setMode("dashboard")}
          />
        ) : activeTab === "menu" ? (
          <RestaurantMenuManager user={user} />
        ) : activeTab === "cuisine" ? (
          <RestaurantOrdersPanel user={user} />
        ) : activeTab === "ventes" ? (
          <div className="space-y-7">
            <PartnerWalletPanel partnerId={user.id} />
            <SalesView
              orders={orders}
              onRefresh={fetchOrdersAndStats}
              onMarkReady={markOrderReady}
            />
          </div>
        ) : (
          <DashboardView
            onUserUpdate={onUserUpdate}
            stats={stats}
            products={products}
            loadingProducts={loadingProducts}
            user={user}
            navigate={navigate}
            onAddProduct={() => setMode("add-product")}
            onDelete={deleteProduct}
            orders={orders}
          />
        )}
      </main>

      {/* BOTTOM NAV */}
      {mode === "dashboard" && (
        <nav className="fixed bottom-0 left-0 right-0 bg-[#020617]/95 backdrop-blur-xl border-t border-white/5 px-5 py-3 flex justify-around items-center z-[100]" data-testid="partner-bottom-nav">
          <BottomTab Icon={Package} label="Stocks" active={activeTab === "stocks"} onClick={() => setActiveTab("stocks")} testId="tab-stocks" />
          {isRestaurant && <BottomTab Icon={UtensilsCrossed} label="Menu" active={activeTab === "menu"} onClick={() => setActiveTab("menu")} testId="tab-menu" />}
          {isRestaurant && <BottomTab Icon={ChefHat} label="Commandes" active={activeTab === "cuisine"} onClick={() => setActiveTab("cuisine")} testId="tab-cuisine" />}
          <BottomTab Icon={ShoppingCart} label="Ventes" active={activeTab === "ventes"} onClick={() => setActiveTab("ventes")} testId="tab-ventes" />
          <BottomTab Icon={Megaphone} label="Vitrines" active={activeTab === "promos"} onClick={() => navigate('/partner/ads')} testId="tab-promos" />
        </nav>
      )}
    </div>
  );
}

// ===================== Sub-views =====================

function DashboardView({ stats, products, loadingProducts, user, navigate, onAddProduct, onDelete, orders, onUserUpdate }) {
  const hasLocation = !!(user?.latitude && user?.longitude);

  // === Today's sales (delivered orders today)
  const todaySales = useMemo(() => {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    return (orders || [])
      .filter((o) => o.status === "delivered" && new Date(o.delivered_at || o.created_at) >= todayStart)
      .reduce((sum, o) => sum + (o.items_amount || o.total_amount || 0), 0);
  }, [orders]);

  // === Unique customers (lifetime)
  const uniqueCustomers = useMemo(() => {
    return new Set((orders || []).map((o) => o.customer_id).filter(Boolean)).size;
  }, [orders]);

  // === Average rating (placeholder until backend exposes per-shop rating)
  const avgRating = user?.rating || 4.92;

  // === 7-day sales chart data
  const salesByDay = useMemo(() => {
    const daysLabels = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
    const result = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() - i);
      const next = new Date(d);
      next.setDate(d.getDate() + 1);
      const sales = (orders || [])
        .filter((o) => {
          if (o.status !== "delivered") return false;
          const od = new Date(o.delivered_at || o.created_at);
          return od >= d && od < next;
        })
        .reduce((sum, o) => sum + (o.items_amount || o.total_amount || 0), 0);
      result.push({ day: daysLabels[d.getDay()], sales });
    }
    return result;
  }, [orders]);

  // === Latest 3 orders (live feed)
  const liveOrders = useMemo(() => {
    return [...(orders || [])]
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 3);
  }, [orders]);

  // === Low stock items
  const lowStockCount = useMemo(() => products.filter((p) => (p.stock ?? 0) <= 3).length, [products]);

  return (
    <div className="space-y-8">
      {/* ============ BENTO STATS ============ */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6" data-testid="partner-stats">
        <BentoStatCard
          title="Ventes du jour"
          value={`${formatFCFA(todaySales)} F`}
          growth={todaySales > 0 ? "Actif" : "—"}
          Icon={TrendingUp}
          color="blue"
          testId="stat-today-sales"
        />
        <BentoStatCard
          title="Commandes"
          value={String(stats.monthlyOrders)}
          growth={stats.pendingOrders > 0 ? `+${stats.pendingOrders} en attente` : "À jour"}
          Icon={Package}
          color="indigo"
          testId="stat-orders-month"
        />
        <BentoStatCard
          title="Clients Fidèles"
          value={formatFCFA(uniqueCustomers)}
          growth={uniqueCustomers > 50 ? "Top" : "Croissance"}
          Icon={Users}
          color="emerald"
          testId="stat-customers"
        />
        <BentoStatCard
          title="Note Boutique"
          value={`${avgRating.toFixed(2)} / 5`}
          growth={avgRating >= 4.5 ? "Top 1%" : "—"}
          Icon={Star}
          color="amber"
          testId="stat-rating"
        />
      </section>

      <SubscriptionWalletCard user={user} />

      <PartnerMapPanel user={user} onUserUpdate={onUserUpdate} />

      {/* ============ WARNING géolocalisation ============ */}
      {!hasLocation && (
        <div className="bg-orange-500/10 border border-orange-500/30 p-4 rounded-2xl flex items-start gap-3">
          <MapPin className="text-orange-400 flex-shrink-0" size={20} />
          <div className="flex-1">
            <p className="text-[11px] font-black text-orange-400 uppercase tracking-widest mb-1">Position non définie</p>
            <p className="text-[10px] text-slate-400 leading-relaxed">Configurez votre position pour apparaître sur la carte des boutiques.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* ============ CHART (8 cols) ============ */}
        <div className="lg:col-span-8 bg-white/5 border border-white/10 rounded-[2.5rem] md:rounded-[3rem] p-6 md:p-8" data-testid="partner-revenue-chart">
          <div className="flex justify-between items-center mb-6 md:mb-8">
            <div>
              <h3 className="text-lg md:text-xl font-black italic uppercase tracking-tight">Analyse de Croissance</h3>
              <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-1">Revenus Hebdomadaires · FCFA</p>
            </div>
            <span className="text-[9px] md:text-[10px] font-black text-blue-400 uppercase tracking-widest bg-blue-500/10 px-3 py-1.5 rounded-full border border-blue-500/30">
              7 derniers jours
            </span>
          </div>
          <div className="h-[240px] md:h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={salesByDay} margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
                <defs>
                  <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2563eb" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="day" stroke="#475569" fontSize={10} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ backgroundColor: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "12px", fontSize: "11px" }}
                  labelStyle={{ color: "#94a3b8" }}
                  formatter={(v) => [`${formatFCFA(v)} F`, "Ventes"]}
                />
                <Area type="monotone" dataKey="sales" stroke="#2563eb" strokeWidth={3} fillOpacity={1} fill="url(#colorSales)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* ============ LIVE FEED (4 cols) ============ */}
        <div className="lg:col-span-4 space-y-5">
          <div className="bg-white/5 border border-white/10 rounded-[2rem] md:rounded-[2.5rem] p-6 md:p-7">
            <h4 className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500 mb-5 flex items-center gap-2">
              <Zap size={14} className="text-blue-500" /> Préparation en direct
            </h4>
            {liveOrders.length === 0 ? (
              <p className="text-[10px] text-slate-600 italic py-6 text-center">Aucune commande récente</p>
            ) : (
              <div className="space-y-5">
                {liveOrders.map((o) => (
                  <LiveOrderItem key={o.id} order={o} />
                ))}
              </div>
            )}
            <button
              onClick={() => toast.info("Historique complet : bientôt disponible")}
              className="w-full mt-6 py-3 border border-white/10 rounded-2xl text-[10px] font-black uppercase tracking-widest text-slate-400 hover:bg-white/5 hover:text-white transition-all"
              data-testid="view-all-history-btn"
            >
              Voir tout l'historique
            </button>
          </div>

          {/* Stock alert */}
          {lowStockCount > 0 && (
            <div className="bg-gradient-to-br from-amber-600/20 to-orange-600/10 border border-amber-500/30 rounded-[1.5rem] p-5" data-testid="low-stock-alert">
              <div className="flex items-center gap-3">
                <div className="bg-amber-500 p-2 rounded-xl text-black flex-shrink-0">
                  <BarChart3 size={18} />
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase text-amber-400 tracking-widest">Alerte Stock Bas</p>
                  <p className="text-[11px] font-bold text-amber-100/90 mt-0.5">{lowStockCount} article{lowStockCount > 1 ? "s" : ""} en rupture imminente</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ============ INVENTAIRE ============ */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-black text-sm tracking-widest uppercase text-white">Mon Inventaire</h2>
          <button
            onClick={onAddProduct}
            className="bg-blue-600 hover:bg-blue-500 text-white px-5 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest shadow-lg shadow-blue-600/20 active:scale-95 flex items-center gap-2"
            data-testid="partner-add-product-btn"
          >
            <Plus size={16} strokeWidth={3} /> Nouveau Produit
          </button>
        </div>

        {loadingProducts ? (
          <div className="flex items-center justify-center py-16 text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : products.length === 0 ? (
          <EmptyState onAddProduct={onAddProduct} />
        ) : (
          <div className="space-y-3" data-testid="partner-products-list">
            {products.map((p) => (
              <ProductCard key={p.id} product={p} onDelete={() => onDelete(p.id)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AddProductView({ formData, setFormData, uploadingImage, submitting, fileInputRef, handleImageUpload, onSubmit, onBack }) {
  return (
    <form onSubmit={onSubmit} className="animate-in slide-in-from-right duration-300 space-y-6" data-testid="partner-add-product-form">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-2 text-[10px] font-black text-[#D4AF37] mb-2 uppercase tracking-widest hover:opacity-80"
        data-testid="add-product-back-btn"
      >
        <ArrowLeft size={16} /> Retour Inventaire
      </button>

      <h2 className="text-2xl font-black italic uppercase" style={{ fontFamily: "Outfit, sans-serif" }}>
        Nouveau <span className="text-[#D4AF37]">Produit</span>
      </h2>

      {/* Upload Photo */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleImageUpload}
        data-testid="product-image-input"
      />
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploadingImage}
        className="w-full h-56 border-2 border-dashed border-[#D4AF37]/30 rounded-[2.5rem] bg-[#1E293B] flex flex-col items-center justify-center hover:bg-[#D4AF37]/5 hover:border-[#D4AF37]/60 transition-all overflow-hidden relative"
        data-testid="product-image-upload-btn"
      >
        {uploadingImage ? (
          <>
            <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin mb-2" />
            <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Téléchargement…</p>
          </>
        ) : formData.image_url ? (
          <>
            <img src={formData.image_url} alt="produit" className="absolute inset-0 w-full h-full object-cover opacity-90" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/80 via-transparent to-transparent" />
            <span className="relative z-10 mt-auto mb-3 bg-[#0F172A]/80 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest text-[#D4AF37]">Changer l'image</span>
          </>
        ) : (
          <>
            <Camera size={32} className="text-[#D4AF37] mb-3" />
            <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Ajouter une photo</p>
          </>
        )}
      </button>

      {/* Champs */}
      <div className="space-y-4">
        <FieldBox label="Nom de la création">
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Ex: Savon Artisanal Premium"
            className="bg-transparent w-full outline-none font-bold text-sm text-white placeholder:text-slate-600"
            required
            data-testid="product-name-input"
          />
        </FieldBox>

        <div className="grid grid-cols-2 gap-4">
          <FieldBox label="Prix (FCFA)">
            <input
              type="number"
              min="0"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              placeholder="0"
              className="bg-transparent w-full outline-none font-bold text-sm text-white placeholder:text-slate-600"
              required
              data-testid="product-price-input"
            />
          </FieldBox>
          <FieldBox label="Stock">
            <input
              type="number"
              min="0"
              value={formData.stock}
              onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
              placeholder="1"
              className="bg-transparent w-full outline-none font-bold text-sm text-white placeholder:text-slate-600"
              required
              data-testid="product-stock-input"
            />
          </FieldBox>
        </div>

        <FieldBox label="Description (optionnel)">
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Décrivez votre produit en quelques mots…"
            rows={3}
            className="bg-transparent w-full outline-none font-medium text-xs text-white placeholder:text-slate-600 resize-none"
            data-testid="product-description-input"
          />
        </FieldBox>

        <FieldBox label="Catégorie">
          <select
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            className="bg-transparent w-full outline-none font-bold text-sm text-white"
            data-testid="product-category-input"
          >
            <option value="boutique" className="bg-[#1E293B]">🛍️ Boutique</option>
            <option value="restaurant" className="bg-[#1E293B]">🍽️ Restaurant / Food</option>
            <option value="pharmacie" className="bg-[#1E293B]">💊 Pharmacie</option>
            <option value="supermarche" className="bg-[#1E293B]">🛒 Supermarché</option>
            <option value="autre" className="bg-[#1E293B]">📦 Autre</option>
          </select>
        </FieldBox>
      </div>

      {/* Rappel Escrow */}
      <div className="bg-[#003594]/10 border border-[#003594]/30 p-5 rounded-[2rem] flex gap-4 items-center">
        <ShieldCheck className="text-[#003594] flex-shrink-0" size={24} />
        <p className="text-[10px] text-slate-300 leading-relaxed">
          Vos fonds sont sécurisés par le système <strong className="text-[#D4AF37]">Escrow ARTEKNOS</strong> jusqu'à confirmation de livraison par le client.
        </p>
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="w-full bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0F172A] py-5 rounded-[2rem] font-black text-xs uppercase tracking-[0.3em] shadow-xl shadow-[#D4AF37]/30 hover:opacity-90 disabled:opacity-50 active:scale-95 transition-all flex items-center justify-center gap-2"
        data-testid="product-submit-btn"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        {submitting ? "Mise en ligne…" : "Lancer la mise en ligne"}
      </button>
    </form>
  );
}

// ===================== SALES VIEW (Page Ventes) =====================

const PERIOD_OPTIONS = [
  { key: "today", label: "Aujourd'hui" },
  { key: "7d", label: "7 jours" },
  { key: "30d", label: "30 jours" },
  { key: "all", label: "Tout" },
];

const STATUS_OPTIONS = [
  { key: "all", label: "Tous", color: "text-white" },
  { key: "pending", label: "En attente", color: "text-orange-400" },
  { key: "preparing", label: "Préparation", color: "text-blue-400" },
  { key: "ready", label: "Prête", color: "text-cyan-400" },
  { key: "assigned", label: "Assignée", color: "text-indigo-400" },
  { key: "picked_up", label: "Récupérée", color: "text-indigo-400" },
  { key: "in_transit", label: "En route", color: "text-amber-400" },
  { key: "delivered", label: "Livrées", color: "text-emerald-400" },
  { key: "cancelled", label: "Annulées", color: "text-red-400" },
];

const STATUS_BADGE = {
  pending: { bg: "bg-orange-500/15", border: "border-orange-500/40", text: "text-orange-300", label: "EN ATTENTE" },
  preparing: { bg: "bg-blue-500/15", border: "border-blue-500/40", text: "text-blue-300", label: "PRÉPARATION" },
  ready: { bg: "bg-cyan-500/15", border: "border-cyan-500/40", text: "text-cyan-300", label: "PRÊTE" },
  assigned: { bg: "bg-indigo-500/15", border: "border-indigo-500/40", text: "text-indigo-300", label: "ASSIGNÉE" },
  picked_up: { bg: "bg-indigo-500/15", border: "border-indigo-500/40", text: "text-indigo-300", label: "RÉCUPÉRÉE" },
  in_transit: { bg: "bg-amber-500/15", border: "border-amber-500/40", text: "text-amber-300", label: "EN ROUTE" },
  delivered: { bg: "bg-emerald-500/15", border: "border-emerald-500/40", text: "text-emerald-300", label: "LIVRÉE" },
  cancelled: { bg: "bg-red-500/15", border: "border-red-500/40", text: "text-red-300", label: "ANNULÉE" },
};

function periodCutoffMs(period) {
  const now = Date.now();
  if (period === "today") {
    const d = new Date(); d.setHours(0, 0, 0, 0);
    return d.getTime();
  }
  if (period === "7d") return now - 7 * 24 * 3600 * 1000;
  if (period === "30d") return now - 30 * 24 * 3600 * 1000;
  return 0;
}

function SalesView({ orders, onRefresh, onMarkReady }) {
  const [period, setPeriod] = useState("7d");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  const filteredOrders = useMemo(() => {
    const cutoff = periodCutoffMs(period);
    const q = search.trim().toLowerCase();
    return (orders || [])
      .filter((o) => {
        const t = new Date(o.created_at).getTime();
        if (cutoff && t < cutoff) return false;
        if (statusFilter !== "all" && o.status !== statusFilter) return false;
        if (q) {
          const hay = `${o.id} ${o.customer_name || ""} ${o.customer_phone || ""} ${o.recipient_name || ""}`.toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return true;
      })
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [orders, period, statusFilter, search]);

  const kpi = useMemo(() => {
    const delivered = filteredOrders.filter((o) => o.status === "delivered");
    const pending = filteredOrders.filter((o) => ["pending", "preparing"].includes(o.status));
    const revenue = delivered.reduce((s, o) => s + (o.items_amount || o.total_amount || 0), 0);
    const aov = delivered.length > 0 ? revenue / delivered.length : 0;
    return {
      revenue,
      ordersCount: filteredOrders.length,
      delivered: delivered.length,
      pending: pending.length,
      aov,
    };
  }, [filteredOrders]);

  return (
    <div className="space-y-7 animate-in fade-in duration-300" data-testid="partner-sales-view">
      {/* ============ HEADER ============ */}
      <div className="flex justify-between items-end gap-4 flex-wrap">
        <div>
          <h2
            className="text-2xl md:text-3xl font-black italic uppercase tracking-tight text-white"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Mes <span className="text-[#D4AF37]">Ventes</span>
          </h2>
          <p className="text-[10px] text-slate-500 font-bold uppercase tracking-[0.3em] mt-1">
            Suivi des commandes & revenus
          </p>
        </div>
        <button
          onClick={onRefresh}
          className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-300 hover:text-white transition-colors"
          data-testid="sales-refresh-btn"
        >
          <RefreshCw size={14} /> Actualiser
        </button>
      </div>

      {/* ============ KPI CARDS ============ */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5" data-testid="sales-kpi">
        <KpiSalesCard
          title="CA réalisé"
          value={`${formatFCFA(kpi.revenue)} F`}
          sub={`Panier moy. ${formatFCFA(Math.round(kpi.aov))} F`}
          Icon={TrendingUp}
          accent="emerald"
          testId="kpi-revenue"
        />
        <KpiSalesCard
          title="Commandes"
          value={String(kpi.ordersCount)}
          sub={`${kpi.delivered} livrées`}
          Icon={ShoppingCart}
          accent="blue"
          testId="kpi-orders"
        />
        <KpiSalesCard
          title="Livrées"
          value={String(kpi.delivered)}
          sub={kpi.ordersCount > 0 ? `${Math.round((kpi.delivered / kpi.ordersCount) * 100)}% du total` : "—"}
          Icon={CheckCircle2}
          accent="indigo"
          testId="kpi-delivered"
        />
        <KpiSalesCard
          title="À traiter"
          value={String(kpi.pending)}
          sub={kpi.pending > 0 ? "Action requise" : "À jour"}
          Icon={Clock}
          accent="amber"
          testId="kpi-pending"
        />
      </section>

      {/* ============ FILTERS ============ */}
      <div className="bg-white/5 border border-white/10 rounded-[1.75rem] p-4 md:p-5 space-y-4" data-testid="sales-filters">
        {/* Period chips */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          <span className="text-[9px] font-black uppercase tracking-[0.3em] text-slate-500 flex-shrink-0">Période</span>
          {PERIOD_OPTIONS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest border whitespace-nowrap transition-colors ${
                period === p.key
                  ? "bg-[#D4AF37] text-[#0F172A] border-[#D4AF37]"
                  : "bg-transparent text-slate-400 border-white/10 hover:bg-white/5 hover:text-white"
              }`}
              data-testid={`period-${p.key}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Status chips */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          <span className="text-[9px] font-black uppercase tracking-[0.3em] text-slate-500 flex-shrink-0 flex items-center gap-1">
            <Filter size={11} /> Statut
          </span>
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s.key}
              onClick={() => setStatusFilter(s.key)}
              className={`px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest border whitespace-nowrap transition-colors ${
                statusFilter === s.key
                  ? "bg-white text-[#0F172A] border-white"
                  : `bg-transparent ${s.color} border-white/10 hover:bg-white/5`
              }`}
              data-testid={`status-${s.key}`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="flex items-center gap-3 bg-[#0F172A] px-4 py-3 rounded-xl border border-white/10">
          <Search size={14} className="text-slate-500 flex-shrink-0" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par n° commande, nom ou téléphone…"
            className="bg-transparent border-none outline-none text-xs w-full text-white placeholder:text-slate-600"
            data-testid="sales-search-input"
          />
        </div>
      </div>

      {/* ============ ORDERS LIST ============ */}
      <div data-testid="sales-orders-list">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-black text-xs tracking-[0.3em] uppercase text-white">
            {filteredOrders.length} commande{filteredOrders.length > 1 ? "s" : ""}
          </h3>
        </div>

        {filteredOrders.length === 0 ? (
          <div className="bg-[#1E293B] border border-white/10 rounded-[2.5rem] py-14 px-6 text-center" data-testid="sales-empty">
            <ShoppingCart className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-sm text-slate-400 font-bold">Aucune commande ne correspond à vos filtres</p>
            <p className="text-[10px] text-slate-500 mt-2 uppercase tracking-widest">
              Essayez d'élargir la période ou de réinitialiser les filtres
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredOrders.map((o) => (
              <SaleOrderCard key={o.id} order={o} onMarkReady={onMarkReady} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function KpiSalesCard({ title, value, sub, Icon, accent = "blue", testId }) {
  const palette = {
    blue: { bg: "bg-blue-500/10", text: "text-blue-400", border: "border-blue-500/30" },
    emerald: { bg: "bg-emerald-500/10", text: "text-emerald-400", border: "border-emerald-500/30" },
    indigo: { bg: "bg-indigo-500/10", text: "text-indigo-400", border: "border-indigo-500/30" },
    amber: { bg: "bg-amber-500/10", text: "text-amber-400", border: "border-amber-500/30" },
  }[accent] || { bg: "bg-blue-500/10", text: "text-blue-400", border: "border-blue-500/30" };

  return (
    <div
      className={`bg-white/5 border ${palette.border} rounded-[1.5rem] md:rounded-[2rem] p-5 md:p-6 hover:bg-white/[0.08] transition-all`}
      data-testid={testId}
    >
      <div className={`w-11 h-11 md:w-12 md:h-12 rounded-2xl mb-4 flex items-center justify-center ${palette.text} ${palette.bg}`}>
        <Icon size={20} />
      </div>
      <p className="text-slate-500 text-[9px] md:text-[10px] font-black uppercase tracking-widest mb-1">{title}</p>
      <h3 className="text-lg md:text-2xl font-black italic tracking-tighter text-white truncate">{value}</h3>
      <p className="text-[9px] md:text-[10px] text-slate-400 font-bold mt-1 truncate">{sub}</p>
    </div>
  );
}

function SaleOrderCard({ order, onMarkReady }) {
  const badge = STATUS_BADGE[order.status] || { bg: "bg-slate-500/15", border: "border-slate-500/40", text: "text-slate-300", label: order.status?.toUpperCase() };
  const created = new Date(order.created_at);
  const diffMin = Math.floor((Date.now() - created.getTime()) / 60000);
  const timeLabel =
    diffMin < 1 ? "À l'instant"
      : diffMin < 60 ? `Il y a ${diffMin} min`
      : diffMin < 1440 ? `Il y a ${Math.floor(diffMin / 60)}h`
      : created.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

  const itemsCount = order.items?.length || 0;
  const itemsLabel = itemsCount === 0
    ? "Livraison simple"
    : itemsCount === 1
      ? (order.items[0].name || "1 article")
      : `${itemsCount} articles`;

  const canMarkReady = ["pending", "preparing"].includes(order.status);

  return (
    <div
      className="bg-[#0F172A] border border-white/10 rounded-[2rem] p-5 md:p-6 hover:border-white/20 transition-colors"
      data-testid={`sale-order-${order.id}`}
    >
      <div className="flex justify-between items-start gap-4 flex-wrap">
        {/* Left - Order ID + items */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-[10px] font-black text-[#D4AF37] uppercase tracking-[0.3em]">
              #{order.id.substring(0, 8).toUpperCase()}
            </span>
            <span className={`text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full border ${badge.bg} ${badge.border} ${badge.text}`}>
              {badge.label}
            </span>
          </div>
          <p className="text-base md:text-lg font-black text-white truncate">{itemsLabel}</p>
          <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-1 flex items-center gap-2">
            <Clock size={10} /> {timeLabel}
          </p>
        </div>

        {/* Right - Amount */}
        <div className="text-right flex-shrink-0">
          <p className="text-[9px] font-black uppercase tracking-widest text-slate-500">Total</p>
          <p className="text-xl md:text-2xl font-black text-emerald-400 italic tracking-tight">
            {formatFCFA(order.items_amount || order.total_amount || 0)}
            <span className="text-[10px] ml-1 text-slate-400">F</span>
          </p>
        </div>
      </div>

      {/* Customer + Delivery */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-5 pt-5 border-t border-white/5">
        <div className="bg-white/[0.03] rounded-2xl p-3 border border-white/5">
          <p className="text-[9px] font-black text-[#D4AF37] uppercase tracking-[0.3em] mb-1">Client</p>
          <p className="text-sm font-black text-white truncate">{order.customer_name || "—"}</p>
          {order.customer_phone && (
            <a
              href={`tel:${order.customer_phone}`}
              className="text-[11px] font-bold text-blue-400 hover:text-blue-300 flex items-center gap-1.5 mt-1"
              data-testid={`sale-order-${order.id}-call`}
            >
              <Phone size={11} /> {order.customer_phone}
            </a>
          )}
        </div>
        <div className="bg-white/[0.03] rounded-2xl p-3 border border-white/5">
          <p className="text-[9px] font-black text-emerald-400 uppercase tracking-[0.3em] mb-1">Livraison</p>
          <p className="text-sm font-black text-white truncate">{order.delivery_address || "—"}</p>
          {order.courier_name && (
            <p className="text-[10px] font-bold text-slate-400 mt-1 truncate">
              Livreur · {order.courier_name}
            </p>
          )}
        </div>
      </div>

      {/* Action footer */}
      {canMarkReady && (
        <button
          onClick={() => onMarkReady(order.id)}
          className="mt-5 w-full bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0F172A] py-3.5 rounded-2xl font-black text-xs uppercase tracking-[0.3em] shadow-lg shadow-[#D4AF37]/20 hover:opacity-90 active:scale-95 transition-all flex items-center justify-center gap-2"
          data-testid={`sale-order-${order.id}-mark-ready`}
        >
          <CheckCircle2 size={16} strokeWidth={3} /> Confirmer l'expédition
          <ChevronRight size={16} strokeWidth={3} />
        </button>
      )}
    </div>
  );
}

// ===================== Small UI atoms =====================

const COLOR_MAP = {
  blue: { text: "text-blue-400", bg: "bg-blue-500/10" },
  indigo: { text: "text-indigo-400", bg: "bg-indigo-500/10" },
  emerald: { text: "text-emerald-400", bg: "bg-emerald-500/10" },
  amber: { text: "text-amber-400", bg: "bg-amber-500/10" },
};

function BentoStatCard({ title, value, growth, Icon, color = "blue", testId }) {
  const c = COLOR_MAP[color] || COLOR_MAP.blue;
  return (
    <div
      className="bg-white/5 border border-white/10 rounded-[1.5rem] md:rounded-[2rem] p-5 md:p-6 hover:bg-white/[0.08] hover:border-white/20 transition-all group cursor-default"
      data-testid={testId}
    >
      <div className={`w-11 h-11 md:w-12 md:h-12 rounded-2xl mb-4 md:mb-6 flex items-center justify-center ${c.text} ${c.bg} group-hover:scale-110 transition-transform`}>
        <Icon size={20} />
      </div>
      <p className="text-slate-500 text-[9px] md:text-[10px] font-black uppercase tracking-widest mb-1">{title}</p>
      <div className="flex justify-between items-end gap-2">
        <h3 className="text-lg md:text-2xl font-black italic tracking-tighter text-white truncate">{value}</h3>
        <span className="text-emerald-400 text-[9px] md:text-[10px] font-black uppercase flex-shrink-0">{growth}</span>
      </div>
    </div>
  );
}

function LiveOrderItem({ order }) {
  // Map status → color & label
  const statusMap = {
    pending: { color: "text-orange-400", label: "En attente" },
    preparing: { color: "text-blue-400", label: "Préparation" },
    ready: { color: "text-blue-400", label: "Prêt" },
    assigned: { color: "text-indigo-400", label: "Livreur assigné" },
    picked_up: { color: "text-indigo-400", label: "Récupéré" },
    in_transit: { color: "text-amber-400", label: "En route" },
    delivered: { color: "text-emerald-400", label: "Livré" },
    cancelled: { color: "text-red-400", label: "Annulé" },
  };
  const s = statusMap[order.status] || { color: "text-slate-400", label: order.status };
  const created = new Date(order.created_at);
  const diffMs = Date.now() - created.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const timeLabel = diffMin < 1 ? "À l'instant" : diffMin < 60 ? `Il y a ${diffMin} min` : `Il y a ${Math.floor(diffMin / 60)}h`;

  const itemLabel = order.items && order.items.length > 0
    ? order.items[0].name || `Commande #${order.id.substring(0, 6)}`
    : `Commande #${order.id.substring(0, 6)}`;

  return (
    <div className="flex justify-between items-center group" data-testid={`live-order-${order.id}`}>
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className={`w-9 h-9 bg-white/5 rounded-xl flex items-center justify-center ${s.color} group-hover:scale-110 transition-transform flex-shrink-0`}>
          <CheckCircle2 size={16} />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] font-black uppercase tracking-tight text-white truncate">{itemLabel}</p>
          <p className={`text-[9px] font-bold uppercase tracking-widest ${s.color}`}>
            #{order.id.substring(0, 5).toUpperCase()} · {s.label}
          </p>
        </div>
      </div>
      <div className="text-right flex-shrink-0 ml-2">
        <p className="text-[10px] font-black text-blue-400">{formatFCFA(order.items_amount || order.total_amount || 0)} F</p>
        <p className="text-[8px] text-slate-600 uppercase font-bold tracking-widest">{timeLabel}</p>
      </div>
    </div>
  );
}

function FieldBox({ label, children }) {
  return (
    <label className="block bg-[#1E293B] p-4 rounded-2xl border border-white/5 hover:border-[#D4AF37]/30 transition-colors">
      <span className="text-[9px] font-black text-slate-500 uppercase mb-2 block tracking-widest">{label}</span>
      {children}
    </label>
  );
}

function BottomTab({ Icon, label, active, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center gap-1 transition-colors ${active ? "text-[#D4AF37]" : "text-slate-500 hover:text-white"}`}
      data-testid={testId}
    >
      <Icon size={20} />
      <span className="text-[8px] font-black uppercase tracking-widest">{label}</span>
    </button>
  );
}

function ProductCard({ product, onDelete }) {
  const stockLow = (product.stock ?? 0) <= 0;
  return (
    <div className="bg-[#1E293B] p-4 rounded-3xl border border-white/5 flex gap-4 items-center hover:border-[#D4AF37]/30 transition-colors group" data-testid={`product-${product.id}`}>
      <div className="w-20 h-20 bg-[#0F172A] rounded-2xl border border-white/5 overflow-hidden flex items-center justify-center flex-shrink-0">
        {product.image_url ? (
          <img src={product.image_url} alt={product.name} className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        ) : (
          <ImageIcon size={28} className="text-slate-700" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-black text-xs uppercase tracking-tight text-white truncate">{product.name}</h4>
        <p className="text-[#D4AF37] font-black text-sm mt-1">{formatFCFA(product.price)} <span className="text-[10px]">FCFA</span></p>
        <div className="flex gap-3 mt-2">
          <span className="text-[9px] font-bold text-slate-500 flex items-center gap-1"><Eye size={10} /> {product.views_count || 0}</span>
          <span className={`text-[9px] font-bold flex items-center gap-1 ${stockLow ? "text-red-400" : "text-slate-500"}`}>
            <Package size={10} /> Stock: {product.stock ?? 0}
          </span>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <button
          className="p-2 bg-white/5 hover:bg-white/10 rounded-lg text-slate-400 hover:text-[#D4AF37]"
          aria-label="Modifier"
          data-testid={`product-${product.id}-edit`}
        >
          <Edit3 size={14} />
        </button>
        <button
          onClick={onDelete}
          className="p-2 bg-red-500/10 hover:bg-red-500/20 rounded-lg text-red-400 hover:text-red-300"
          aria-label="Supprimer"
          data-testid={`product-${product.id}-delete`}
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

function EmptyState({ onAddProduct }) {
  return (
    <div className="bg-[#1E293B] border border-white/10 rounded-[2.5rem] py-12 px-6 text-center" data-testid="partner-empty-state">
      <StoreIcon className="w-12 h-12 text-slate-600 mx-auto mb-3" />
      <p className="text-sm text-slate-400 mb-2 font-bold">Aucun produit dans votre inventaire</p>
      <p className="text-[10px] text-slate-500 mb-6 uppercase tracking-widest">Ajoutez votre première création pour commencer à vendre</p>
      <button
        onClick={onAddProduct}
        className="inline-flex items-center gap-2 bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0F172A] px-6 py-3 rounded-full font-black text-xs uppercase tracking-widest shadow-lg shadow-[#D4AF37]/20 hover:opacity-90 active:scale-95"
      >
        <Plus size={16} strokeWidth={3} />
        Ajouter un produit
      </button>
    </div>
  );
}
